/**
 * O que faz: Centraliza séries temporais e a atribuição exclusiva usada pelo relatório.
 * Onde usa: Relatório exportável e gráficos do dashboard.
 * Conexões: date-fns e dashboardReportTypes.
 * Tabelas: Nenhuma; processa leads já carregados em memória.
 * Data: 27/07/2026.
 */

import { eachDayOfInterval, endOfDay, format, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type {
  DashboardDailyOriginPoint,
  DashboardOrigin,
  DashboardWeekdayPoint,
} from './dashboardReportTypes';

interface ReportLead {
  created_at?: string | null;
  ad_name?: string | null;
  anuncio?: string | null;
  link_name?: string | null;
}

const hasText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export const classifyLeadOrigin = (lead: ReportLead): DashboardOrigin => {
  // O anúncio tem prioridade para que cada contato pertença a uma única origem e a soma sempre feche com o total.
  if (hasText(lead.ad_name) || hasText(lead.anuncio)) return 'ads';
  if (hasText(lead.link_name)) return 'links';
  return 'other';
};

export const buildDailyOriginSeries = (
  leads: ReportLead[],
  startDate: Date | null,
  endDate: Date | null,
  maxDays = 90,
): DashboardDailyOriginPoint[] => {
  const limiteDePontos = Math.max(1, maxDays);
  const leadsByDay = new Map<string, { total: number; ads: number; links: number; other: number }>();
  let menorData: number | null = null;
  let maiorData: number | null = null;

  leads.forEach((lead) => {
    if (!lead.created_at) return;
    const data = new Date(lead.created_at);
    if (!Number.isFinite(data.getTime())) return;
    const timestamp = data.getTime();
    menorData = menorData === null ? timestamp : Math.min(menorData, timestamp);
    maiorData = maiorData === null ? timestamp : Math.max(maiorData, timestamp);
    const dateKey = format(data, 'yyyy-MM-dd');
    const atual = leadsByDay.get(dateKey) ?? { total: 0, ads: 0, links: 0, other: 0 };
    const origem = classifyLeadOrigin(lead);
    atual.total += 1;
    atual[origem] += 1;
    leadsByDay.set(dateKey, atual);
  });

  // No filtro “Máximo”, as datas chegam nulas porque a consulta representa todo o histórico.
  // Inferir os limites nos próprios leads evita mostrar somente os últimos 13 dias no gráfico.
  const inicioInferido = menorData === null
    ? new Date(Date.now() - 13 * 86_400_000)
    : new Date(menorData);
  const fimInferido = maiorData === null ? new Date() : new Date(maiorData);
  const start = startOfDay(startDate ?? inicioInferido);
  const end = endOfDay(endDate ?? fimInferido);
  const days = eachDayOfInterval({ start, end });

  const montarPonto = (inicio: Date, fim: Date, isInterval = false): DashboardDailyOriginPoint => {
    const inicioKey = format(inicio, 'yyyy-MM-dd');
    const fimKey = format(fim, 'yyyy-MM-dd');
    const total = { total: 0, ads: 0, links: 0, other: 0 };

    // Soma somente os dias do intervalo; assim o gráfico mantém o total mesmo com
    // períodos de anos e não precisa renderizar centenas de barras quase invisíveis.
    eachDayOfInterval({ start: inicio, end: fim }).forEach((dia) => {
      const contagem = leadsByDay.get(format(dia, 'yyyy-MM-dd'));
      if (!contagem) return;
      total.total += contagem.total;
      total.ads += contagem.ads;
      total.links += contagem.links;
      total.other += contagem.other;
    });

    return {
      dateKey: isInterval ? `${inicioKey}/${fimKey}` : inicioKey,
      label: isInterval
        ? `${format(inicio, 'dd/MM', { locale: ptBR })}–${format(fim, 'dd/MM', { locale: ptBR })}`
        : format(inicio, 'dd/MM', { locale: ptBR }),
      ...total,
      ...(isInterval ? { isInterval: true } : {}),
    };
  };

  if (days.length <= limiteDePontos) {
    return days.map((day) => montarPonto(day, day));
  }

  const tamanhoDoIntervalo = Math.ceil(days.length / limiteDePontos);
  const pontos: DashboardDailyOriginPoint[] = [];
  for (let inicio = 0; inicio < days.length; inicio += tamanhoDoIntervalo) {
    const fim = Math.min(inicio + tamanhoDoIntervalo - 1, days.length - 1);
    pontos.push(montarPonto(days[inicio], days[fim], true));
  }

  return pontos;
};

const WEEKDAYS = [
  ['Domingo', 'Dom'],
  ['Segunda', 'Seg'],
  ['Terça', 'Ter'],
  ['Quarta', 'Qua'],
  ['Quinta', 'Qui'],
  ['Sexta', 'Sex'],
  ['Sábado', 'Sáb'],
] as const;

export const buildWeekdaySeries = (leads: ReportLead[]): DashboardWeekdayPoint[] => {
  const counts = new Array<number>(7).fill(0);
  leads.forEach((lead) => {
    if (lead.created_at) counts[new Date(lead.created_at).getDay()] += 1;
  });

  return WEEKDAYS.map(([label, shortLabel], dayIndex) => ({
    dayIndex,
    label,
    shortLabel,
    leads: counts[dayIndex],
    percentage: leads.length > 0 ? Math.round((counts[dayIndex] / leads.length) * 1000) / 10 : 0,
  }));
};
